import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NavParams } from "ionic-angular";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import SelectFormInput from "../../../../models/SelectFormInput";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { OrderForm } from "../order-form/order-form";

@Component({
  selector: "level-of-care-order-form",
  templateUrl: "level-of-care-order-form.html",
})
export class LevelOfCareOrderFormComponent extends OrderForm implements OnInit {
  protected orderForm: FormGroup;
  private textAreaMaxLength = 1000;
  private startDateMin: string = "";
  private startDateMax: string = "";
  private endDateMin: string = "";
  private endDateMax: string = "";

  private facilitiesOptions: any[] = [];
  private facilitySearchTerm: string;
  private facilitiesSearching: boolean;
  private latestSearchTerm: string;

  private invalidFacility: boolean = false;

  private homeSelected: boolean;
  private facilitySelected: boolean = false;
  private facilityInfo = {};

  hideFacilitiesOptions: boolean = true;
  private _patientData: any = {};
  private _existingData: any = {};
  defaultStartTime: string = "";
  newPatientChartData: Object;
  locStartDate: string;
  reasonForGIPOptions: Object;
  continuousCareSymptomsValues: Object;

  hidePhysiciansOptions: Boolean = true;
  searchTerm: string = "";
  selectPhysicianFlag: boolean = false;
  setPhysicianName: boolean = false;

  hideDcPhysiciansOptions: Boolean = true;
  dcSearchTerm: string = "";
  selectDcPhysicianFlag: boolean = false;
  setDcPhysicianName: boolean = false;

  private physicians: any = [];

  @Input() set patientData(data: object) {
    this._patientData = data;
    this.populateFacilityInformation();
  }
  @Input() set data(data: object) {
    //set when editing an order
    this._existingData = data;
    this.setExistingData();
  }
  @Input() levelOfCareOptions: SelectFormInput[] = [];
  @Input() levelOfCareTypeOptions: SelectFormInput[] = [];
  @Input() physicianOptions: SelectFormInput[] = [];
  @Input() physicianOrderOptions: SelectFormInput[] = [];
  @Input() allFacilityOptions: any[] = [];
  @Input() status: any;
  @Input() isOpenOrderEdit: boolean = false;
  @Input() isIpad: boolean = false;
  @Output() onValidityChange: EventEmitter<any> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    public translatePipe: TranslateService,
    private navParams: NavParams,
    private resourceManagementService: ResourceManagementService,
    public utilityService: UtilityService
  ) {
    super();
    this.startDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.startDateMax = moment().format("YYYY-MM-DD");
    this.endDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.endDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.defaultStartTime = moment().format("YYYY-MM-DD");

    this.orderForm = this.formBuilder.group({
      levelOfCare: [
        "",
        Validators.compose([Validators.minLength(1), Validators.required]),
      ],
      levelOfCareType: ["", Validators.compose([Validators.required])],
      facilityName: [""],
      facility: [{}],
      physician: ["", Validators.compose([Validators.required])],
      itemStartDatetime: ["", Validators.compose([Validators.required])],
      acdApproval: [false],
      description: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength)]),
      ],
      physicianOrder: ["", Validators.compose([Validators.required])],
      physicianOrderNotes: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      reasonsForGip: [""],
      symptoms: [""],
      searchTerm: new FormControl(""),
      dcSearchTerm: new FormControl(""),
    });

    this.orderForm.valueChanges.subscribe((data) => {
      console.log(data, this.orderForm.valid);
      this.onValidityChange.emit({
        validity: this.orderForm.valid,
        data: data,
      });
    });

    //enforce character limit
    this.orderForm.controls["description"].valueChanges.subscribe((data) => {
      if (data.length > this.textAreaMaxLength) {
        this.orderForm.controls["description"].setValue(
          data.substring(0, this.textAreaMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["physicianOrderNotes"].valueChanges.subscribe(
      (data) => {
        if (data.length > this.textAreaMaxLength / 2) {
          this.orderForm.controls["physicianOrderNotes"].setValue(
            data.substring(0, this.textAreaMaxLength / 2)
          );
        }
      }
    );

    this.orderForm.controls["levelOfCareType"].valueChanges.subscribe(
      (data) => {
        //Need to handle business logic here if user selects home, than facility selection is disabled
        this.homeSelected = data === "home";
        if (this.homeSelected) {
          this.orderForm.controls["facilityName"].setValue("Home");
          this.orderForm.controls["facility"].setValue(null);
          this.hideFacilitiesOptions = true;
        } else {
          //check if facility is there and if it has a value with wrong type
          this.updateFacilityDetails((facility) => {
            if (facility && facility.facilityDetails) {
              if (data == "unspecified") {
                if (facility.facilityDetails[0]) {
                  this.orderForm.controls["facility"].setValue(null);
                  this.orderForm.controls["facilityName"].setValue("");
                }
              } else {
                if (!facility.facilityDetails[0]) {
                  this.orderForm.controls["facility"].setValue(null);
                  this.orderForm.controls["facilityName"].setValue("");
                } else {
                  let currentLocType = facility.facilityDetails[0].facilityType;
                  if (
                    currentLocType &&
                    currentLocType != this.translatePipe.instant(data)
                  ) {
                    this.orderForm.controls["facility"].setValue(null);
                    this.orderForm.controls["facilityName"].setValue("");
                  }
                }
              }
            }
            // if (this.orderForm.controls['facility'].value && this.orderForm.controls['facility'].value.facilityDetails){
            //     let currentLocType = this.orderForm.controls['facility'].value.facilityDetails[0].facilityType;
            //     if(currentLocType && currentLocType != this.translatePipe.instant(data)){
            //         this.orderForm.controls['facility'].setValue(null);
            //         this.orderForm.controls['facilityName'].setValue("");
            //     }
            // }
          });
        }
        if (
          this.orderForm.controls["facilityName"].value === "Home" &&
          !this.homeSelected
        ) {
          this.orderForm.controls["facilityName"].setValue("");
        }
        this.populateFacilityInformation();
      }
    );

    this.orderForm.controls["facility"].valueChanges.subscribe((data) => {
      this.facilitySelected = data && Object.keys(data).length > 0;
      if (this.homeSelected) {
        this.facilitySelected = true;
      }
    });

    this.orderForm.controls["facilityName"].valueChanges.subscribe((data) => {
      let currentFacility = this.orderForm.controls["facility"].value;
      if (
        currentFacility &&
        Object.keys(currentFacility).length > 0 &&
        data.length > 0
      ) {
        this.invalidFacility = currentFacility["name"] !== data;
      } else {
        this.invalidFacility = false;
      }
      if (
        !this.homeSelected &&
        (!currentFacility || Object.keys(currentFacility).length === 0)
      ) {
        this.invalidFacility = true;
      }
      if (this.homeSelected) this.invalidFacility = false;
      if (this.invalidFacility) {
        this.orderForm.controls["facility"].setValue(null);
        this.orderForm.controls["facilityName"].setErrors({ incorrect: true });
      } else {
        this.orderForm.controls["facilityName"].setErrors(null);
      }
    });

    this.orderForm.controls["levelOfCare"].valueChanges.subscribe((data) => {
      if (data === "generalInpatient") {
        this.orderForm
          .get("reasonsForGip")
          .setValidators([Validators.required]);
        this.orderForm.controls["reasonsForGip"].updateValueAndValidity();
        if (this.orderForm.get("symptoms")) {
          this.orderForm.get("symptoms").setValue(null);
          this.orderForm.get("symptoms").setValidators([]);
          this.orderForm.get("symptoms").updateValueAndValidity();
        }
      }

      if (data === "continuousCare") {
        this.orderForm.get("symptoms").setValidators([Validators.required]);
        this.orderForm.controls["symptoms"].updateValueAndValidity();
        if (this.orderForm.get("reasonsForGip")) {
          this.orderForm.get("reasonsForGip").setValue(null);
          this.orderForm.get("reasonsForGip").setValidators([]);
          this.orderForm.get("reasonsForGip").updateValueAndValidity();
        }
      }
    });
  }

  ngOnInit() {
    if (this._existingData) {
      if (this._existingData.rmPhysician) {
        this.searchTerm = this._existingData.rmPhysician.displayName;
      }
    }

    // if discontinue make itemDiscontinueDatetime required
    if (this.status === "discontinue") {
      this.orderForm.addControl(
        "dcPhysician",
        new FormControl("", Validators.required)
      );
      this.orderForm.controls["dcPhysician"].updateValueAndValidity();
    }

    this.continuousCareSymptomsValues = this.navParams.get(
      "continuousCareSymptomsValues"
    );
    this.reasonForGIPOptions = this.navParams.get("reasonForGIPOptions");
    this.newPatientChartData = this.navParams.get("newPatientChartData");
    if (this.newPatientChartData) {
      if (this.newPatientChartData["levelOfCare"]) {
        this.locStartDate = moment(
          this.newPatientChartData["levelOfCare"]["startDatetime"],
          "YYYY-MM-DD"
        ).format("MM-DD-YYYY");
        this.startDateMin = moment(this.locStartDate, "MM-DD-YYYY").format(
          "YYYY-MM-DD"
        );
      }
    }
  }

  updateFacilityDetails(callback) {
    let fac = this.orderForm.controls["facility"].value;
    if (!fac) return; //if it's HOME
    let foundFac = this.allFacilityOptions.find((f) => f.id === fac.id);
    if (foundFac) {
      this.orderForm.controls["facility"].setValue(foundFac);
      callback(foundFac);
    } else if (Object.keys(fac).length > 0) {
      callback(fac);
    } else {
      callback(null);
    }
  }

  searchFacilities(searchTerm: string) {
    // if(!this.facilitiesOptions) {
    //     this.facilitiesOptions = this.allFacilityOptions;
    //     return;
    // }
    if (
      this.facilitiesOptions.filter((issue) => issue.name === searchTerm)
        .length === 0 &&
      searchTerm !== ""
    ) {
      this.hideFacilitiesOptions = false;
      this.facilitiesSearching = true;

      this.latestSearchTerm = searchTerm;
      if (searchTerm.length > 2) {
        //requirements don't want search to happen until user has entered 3 chars
        let filteredResults = this.getFilteredFacilities(
          searchTerm,
          this.allFacilityOptions
        );
        if (searchTerm === this.latestSearchTerm) {
          this.facilitiesOptions = filteredResults;
          this.facilitiesSearching = false;
          this.facilitySearchTerm = searchTerm;
        }
      } else {
        this.facilitiesOptions = this.getFilteredFacilities(
          "",
          this.allFacilityOptions
        ); //this.allFacilityOptions;
      }
    }
  }

  setExistingData() {
    Object.keys(this.orderForm.controls).forEach((control) => {
      if (this._existingData[control]) {
        if (control == "facility")
          this.setFacilityName(this._existingData[control], true);
        else
          this.orderForm.controls[control].setValue(
            this._existingData[control]
          );
      }
    });
  }

  setFacilityName(facility: any, isEdit: boolean = false) {
    if (
      isEdit &&
      this._existingData["levelOfCareType"] &&
      this._existingData["levelOfCareType"] == "home"
    ) {
      this.orderForm.controls["facility"].setValue(null);
      this.orderForm.controls["facilityName"].setValue("Home");
      this.homeSelected = true;
    } else {
      this.orderForm.controls["facility"].setValue(facility);
      this.orderForm.controls["facilityName"].setValue(facility.name);
      this.homeSelected = false;
    }
    this.invalidFacility = false;
    this.orderForm.controls["facilityName"].setErrors(null);
    //GET PHONE, FAX, EMAIL, and ADDR and populate(where?) those fields
    this.hideFacilitiesOptions = true;
    this.populateFacilityInformation();
  }

  getFilteredFacilities(searchTerm, results) {
    let careLocType = null;
    if (this.orderForm.controls["levelOfCareType"].value) {
      careLocType = this.translatePipe.instant(
        this.orderForm.controls["levelOfCareType"].value
      );
    }
    if (!careLocType || careLocType == "Home") {
      return results.filter((facility) => {
        return facility["name"]
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    } else {
      return results.filter((facility) => {
        return (
          facility["name"].toLowerCase().includes(searchTerm.toLowerCase()) &&
          facility.facilityDetails[0] &&
          facility.facilityDetails[0].facilityType == careLocType
        );
      });
    }
  }

  facilityDataPresent() {
    let fac = this.orderForm.controls["facility"].value;
    return fac && Object.keys(fac).length > 0;
  }

  populateFacilityInformation() {
    if (!this.invalidFacility) {
      if (this.homeSelected) {
        this.facilityInfo = this.getPatientHomeInfo();
      } else {
        if (this.facilityDataPresent()) {
          let facility = this.orderForm.controls["facility"].value;
          this.facilityInfo = {
            phone: this.getFacilityPhone(facility),
            fax: this.getFacilityFax(facility),
            email: this.getFacilityEmail(facility),
            address: this.getFacilityAddress(facility),
          };
        }
      }
    }
  }

  getPatientHomeInfo() {
    let chart = this._patientData["patientChart"];
    if (chart) {
      return {
        phone: chart["homePhone"]
          ? chart["homePhone"]
          : "No Phone Number on File",
        fax: "N/A",
        email: chart["patientEmail"]
          ? chart["patientEmail"]
          : "No Email on File",
        address: this.getHomeAddress(chart),
      };
    }
  }

  getFacilityAddress(facility) {
    let address = facility.streetAddress;
    if (!address) return "No Address on File";
    return (
      address.address1 +
      " " +
      address.address2 +
      ", " +
      address.city +
      ", " +
      address.state +
      " " +
      address.zipCode
    );
  }

  getHomeAddress(chart) {
    if (!chart["homeAddress1"]) return "No Home Address on File";
    return (
      chart["homeAddress1"] +
      " " +
      (chart["homeAddress2"] ? chart["homeAddress2"] : "") +
      ", " +
      (chart["homeCity"] ? chart["homeCity"] : "") +
      ", " +
      (chart["homeState"] ? chart["homeState"] : "") +
      " " +
      (chart["homeZip"] ? chart["homeZip"] : "")
    );
  }

  getFacilityPhone(facility) {
    if (facility.facilityDetails[0]) {
      let phone = facility.facilityDetails[0].workPhone;
      if (phone) return phone.display;
    }
    return "No Phone Number on File";
  }

  getFacilityFax(facility) {
    if (facility.facilityDetails[0]) {
      let fax = facility.facilityDetails[0].fax;
      if (fax) return fax.display;
    }
    return "No Fax Number on File";
  }

  getFacilityEmail(facility) {
    if (facility.facilityDetails[0]) {
      let email = facility.facilityDetails[0].email;
      return email ? email : "No Email on File";
    }
    return "No Email on File";
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hidePhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getPhysicians(searchTerm) {
    if (!this.selectPhysicianFlag) {
      this.selectPhysicianFlag = false;
      this.setPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectPhysicianFlag = false;
    }
  }

  setPhysician(physician: any) {
    this.selectPhysicianFlag = true;
    this.setPhysicianName = false;
    this.hidePhysiciansOptions = true;
    this.orderForm.controls["physician"].setValue(physician);
    this.orderForm.controls["searchTerm"].setValue(physician.displayName);
  }
  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchDcPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hideDcPhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getDcPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getDcPhysicians(searchTerm) {
    if (!this.selectDcPhysicianFlag) {
      this.selectDcPhysicianFlag = false;
      this.setDcPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectDcPhysicianFlag = false;
    }
  }

  setDcPhysician(physician: any) {
    this.selectDcPhysicianFlag = true;
    this.setDcPhysicianName = false;
    this.hideDcPhysiciansOptions = true;
    this.orderForm.controls["dcPhysician"].setValue(physician);
    this.orderForm.controls["dcSearchTerm"].setValue(physician.displayName);
  }
}



// WEBPACK FOOTER //
// ./src/components/orders/forms/level-of-care-order-form/level-of-care-order-form.ts